import { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CollectionList } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import './BatchEditActionBar.css';

// Condition options shared between have and want list
const CONDITION_OPTIONS = [
  { value: '', label: '-- No change --' },
  { value: 'NEAR_MINT', label: 'NM' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP' },
  { value: 'MODERATELY_PLAYED', label: 'MP' },
];

// For want list: selecting a min condition generates the acceptable conditions array
// e.g., selecting LP means NM + LP are acceptable
const CONDITION_HIERARCHY = [
  'NEAR_MINT',
  'SLIGHTLY_PLAYED',
  'MODERATELY_PLAYED',
];

const FOIL_OPTIONS_HAVE = [
  { value: '', label: '-- No change --' },
  { value: 'false', label: 'Non-foil' },
  { value: 'true', label: 'Foil' },
];

const FOIL_OPTIONS_WANT = [
  { value: '', label: '-- No change --' },
  { value: 'any', label: 'Any' },
  { value: 'foilOnly', label: 'Foil Only' },
  { value: 'nonFoilOnly', label: 'Non-Foil Only' },
];

const TRADE_OPTIONS = [
  { value: '', label: '-- No change --' },
  { value: 'true', label: 'For Trade' },
  { value: 'false', label: 'Not for Trade' },
];

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'pt', label: 'PT' },
  { code: 'ja', label: 'JA' },
  { code: 'ko', label: 'KO' },
  { code: 'ru', label: 'RU' },
  { code: 'zhs', label: 'ZHS' },
  { code: 'zht', label: 'ZHT' },
];

export interface HaveListBatchChanges {
  condition?: CardCondition;
  foil?: boolean;
  forTrade?: boolean;
}

export interface WantListBatchChanges {
  acceptableConditions?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  acceptableLanguages?: string[];
}

interface Props {
  selectedCount: number;
  isHaveList: boolean;
  isWantList: boolean;
  selectedRows: CollectionList[];
  isApplying: boolean;
  onApplyHaveList?: (changes: HaveListBatchChanges) => void;
  onApplyWantList?: (changes: WantListBatchChanges) => void;
  onCancel: () => void;
}

export const BatchEditActionBar = ({
  selectedCount,
  isHaveList,
  isWantList,
  selectedRows,
  isApplying,
  onApplyHaveList,
  onApplyWantList,
  onCancel,
}: Props) => {
  // Have list field state
  const [condition, setCondition] = useState('');
  const [foil, setFoil] = useState('');
  const [forTrade, setForTrade] = useState('');

  // Want list field state
  const [wantCondition, setWantCondition] = useState('');
  const [wantFoil, setWantFoil] = useState('');
  const [wantLanguages, setWantLanguages] = useState<string[]>([]);

  // Check if any changes have been made
  const hasChanges = isHaveList
    ? condition !== '' || foil !== '' || forTrade !== ''
    : wantCondition !== '' || wantFoil !== '' || wantLanguages.length > 0;

  // Want list: check for duplicate card warning
  const duplicateWarning = useMemo(() => {
    if (!isWantList || wantCondition === '') return null;

    // Group selected rows by oracleId
    const groups: Record<string, CollectionList[]> = {};
    selectedRows.forEach(row => {
      const key = row.oracleId || row.cardName || '';
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    // Check if any group has rows with different conditions
    const groupKeys = Object.keys(groups);
    for (let i = 0; i < groupKeys.length; i++) {
      const groupRows = groups[groupKeys[i]];
      if (groupRows.length > 1) {
        const conditions = new Set(groupRows.map((r: CollectionList) =>
          JSON.stringify(r.acceptableConditions || [])
        ));
        if (conditions.size > 1) {
          return 'Some selected cards have different conditions set. Applying a single condition will overwrite all of them.';
        }
      }
    }
    return null;
  }, [isWantList, wantCondition, selectedRows]);

  const handleApply = useCallback(() => {
    if (isHaveList && onApplyHaveList) {
      const changes: HaveListBatchChanges = {};
      if (condition) changes.condition = condition as CardCondition;
      if (foil) changes.foil = foil === 'true';
      if (forTrade) changes.forTrade = forTrade === 'true';
      onApplyHaveList(changes);
    }

    if (isWantList && onApplyWantList) {
      const changes: WantListBatchChanges = {};
      if (wantCondition) {
        // Generate acceptable conditions from min condition threshold
        const idx = CONDITION_HIERARCHY.indexOf(wantCondition);
        if (idx !== -1) {
          changes.acceptableConditions = CONDITION_HIERARCHY.slice(0, idx + 1);
        }
      }
      if (wantFoil) {
        if (wantFoil === 'any') {
          changes.acceptFoil = true;
          changes.acceptNonFoil = true;
        } else if (wantFoil === 'foilOnly') {
          changes.acceptFoil = true;
          changes.acceptNonFoil = false;
        } else if (wantFoil === 'nonFoilOnly') {
          changes.acceptFoil = false;
          changes.acceptNonFoil = true;
        }
      }
      if (wantLanguages.length > 0) {
        changes.acceptableLanguages = wantLanguages;
      }
      onApplyWantList(changes);
    }
  }, [isHaveList, isWantList, condition, foil, forTrade, wantCondition, wantFoil, wantLanguages, onApplyHaveList, onApplyWantList]);

  const toggleLanguage = useCallback((code: string) => {
    setWantLanguages(prev =>
      prev.includes(code)
        ? prev.filter(l => l !== code)
        : [...prev, code]
    );
  }, []);

  const bar = (
    <div className="batch-edit-overlay">
      <div className="batch-edit-bar">
        <div className="batch-edit-fields">
          {isHaveList && (
            <>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Condition</span>
                <select
                  className={`batch-edit-select ${condition ? 'changed' : ''}`}
                  value={condition}
                  onChange={e => setCondition(e.target.value)}
                  disabled={isApplying}
                >
                  {CONDITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Foil</span>
                <select
                  className={`batch-edit-select ${foil ? 'changed' : ''}`}
                  value={foil}
                  onChange={e => setFoil(e.target.value)}
                  disabled={isApplying}
                >
                  {FOIL_OPTIONS_HAVE.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Trade Status</span>
                <select
                  className={`batch-edit-select ${forTrade ? 'changed' : ''}`}
                  value={forTrade}
                  onChange={e => setForTrade(e.target.value)}
                  disabled={isApplying}
                >
                  {TRADE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {isWantList && (
            <>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Min. Condition</span>
                <select
                  className={`batch-edit-select ${wantCondition ? 'changed' : ''}`}
                  value={wantCondition}
                  onChange={e => setWantCondition(e.target.value)}
                  disabled={isApplying}
                >
                  {CONDITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Foil Finish</span>
                <select
                  className={`batch-edit-select ${wantFoil ? 'changed' : ''}`}
                  value={wantFoil}
                  onChange={e => setWantFoil(e.target.value)}
                  disabled={isApplying}
                >
                  {FOIL_OPTIONS_WANT.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Language</span>
                <div className="batch-edit-lang-pills">
                  {LANGUAGE_OPTIONS.map(lang => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`batch-edit-lang-pill ${wantLanguages.includes(lang.code) ? 'active' : ''}`}
                      onClick={() => toggleLanguage(lang.code)}
                      disabled={isApplying}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {duplicateWarning && (
            <div className="batch-edit-warning">
              <FontAwesomeIcon icon="triangle-exclamation" />
              <span>{duplicateWarning}</span>
            </div>
          )}
        </div>

        <div className="batch-edit-actions">
          <button
            className="batch-edit-apply-btn"
            onClick={handleApply}
            disabled={!hasChanges || isApplying}
          >
            {isApplying ? (
              <>
                <div className="batch-edit-spinner" />
                Applying...
              </>
            ) : (
              `Apply to ${selectedCount} card${selectedCount !== 1 ? 's' : ''}`
            )}
          </button>
          <button
            className="batch-edit-cancel-btn"
            onClick={onCancel}
            disabled={isApplying}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(bar, document.body);
};
